/**
 * RevenueRegisterYearCloseController — modale de clôture annuelle du livre
 * des recettes (EPIC-016 / 16.6, spec §7.H.G).
 *
 * Pilote la modale `<section id="year-closure">` :
 *   - Action `open` : lit `year` sur le trigger (`data-close-year`),
 *     fallback à l'année calendaire précédente. Charge
 *     `GET /revenue/years/{year}/pre-close-preview` pour pré-remplir
 *     le récap (entriesCount, paymentsAmount, manualEntriesAmount,
 *     correctionsAmount, franchiseAmount, mois non confirmés BIC vente),
 *     puis révèle la modale. Refuse l'ouverture si l'exercice est déjà
 *     clôturé (`alreadyClosed: true`).
 *   - Action `close` : ferme la modale.
 *   - Action `confirm` : `POST /revenue/years/{year}/close`.
 *     Au succès, rechargement complet de la page (le bouton Clôturer
 *     disparaît, l'encart « Clôture des exercices » est mis à jour avec
 *     la nouvelle entrée + lien coffre-fort).
 *
 * Erreurs serveur mappées :
 *   - 422 `business_rule_violation` (year_not_terminated)
 *   - 409 `conflict` (year_already_closed)
 *
 * Zéro HTML inline : toutes les valeurs dynamiques sont écrites via
 * `target.textContent` sur des hooks déjà présents dans la maquette
 * (verbatim §7.H.G).
 */

import BaseController from "./base_controller.js";
import { api, APIError } from "/js/services/api.js";

const ERROR_MESSAGES = {
  year_not_terminated:
    "La clôture n'est disponible qu'à partir du 1er janvier de l'année suivante.",
  year_already_closed: "Cet exercice est déjà clôturé.",
  business_rule_violation: "Cette action n'est pas autorisée.",
  conflict: "Cet exercice est déjà clôturé.",
  validation_failed: "Certains champs sont invalides.",
  unauthorized: "Vous n'êtes pas autorisé à effectuer cette action.",
};

export default class RevenueRegisterYearCloseController extends BaseController {
  static targets = [
    "title",
    "intro",
    "entriesCount",
    "totalInclVat",
    "franchiseAmount",
    "paymentsAmount",
    "manualEntriesAmount",
    "correctionsAmount",
    "unconfirmedBlock",
    "unconfirmedTitle",
    "unconfirmedIntro",
    "unconfirmedDetail",
    "warning",
    "submitButton",
    "errorBox",
    "errorMessage",
  ];

  _year = null;
  _submitting = false;

  async open(event) {
    const trigger = event?.currentTarget ?? null;
    const year = this._readYear(trigger);
    if (!year) {
      this.showError("Impossible de déterminer l'exercice à clôturer.");
      return;
    }
    this._year = year;
    this._clearError();
    this._writeStaticTexts(year);

    let preview;
    try {
      preview = await api.getRevenueYearPreClosePreview(year);
    } catch (error) {
      this._handleApiError(error);
      return;
    }

    if (preview?.alreadyClosed === true) {
      this._showError("Cet exercice est déjà clôturé.");
      this._reload();
      return;
    }

    this._writeSummary(preview ?? {});
    this._showModal();
  }

  close() {
    this._hideModal();
    this._clearError();
    this._submitting = false;
    this._setSubmitButtonState(false);
  }

  async confirm(event) {
    if (event) event.preventDefault();
    if (this._submitting || !this._year) return;

    this._submitting = true;
    this._setSubmitButtonState(true);
    this._clearError();

    try {
      await api.closeRevenueYear(this._year);
      this.showSuccess("Exercice clôturé. PDF annuel archivé dans votre coffre-fort.");
      this._reload();
    } catch (error) {
      this._handleApiError(error);
      this._submitting = false;
      this._setSubmitButtonState(false);
    }
  }

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  _readYear(trigger) {
    if (trigger) {
      const ds = trigger.dataset ?? {};
      const explicit = this._toInt(ds.closeYear ?? ds.year ?? null);
      if (explicit) return explicit;
    }
    // Fallback : année calendaire précédente.
    return new Date().getFullYear() - 1;
  }

  _toInt(value) {
    if (value === null || value === undefined || value === "") return null;
    const n = Number.parseInt(String(value), 10);
    return Number.isFinite(n) && n > 0 ? n : null;
  }

  _writeStaticTexts(year) {
    if (this.hasTitleTarget) {
      this.titleTarget.textContent = `Clôturer définitivement l'exercice ${year}`;
    }
    if (this.hasIntroTarget) {
      this.introTarget.textContent =
        `Récapitulatif de l'exercice ${year} avant scellement définitif et ` +
        `archivage du PDF annuel dans votre coffre-fort.`;
    }
    if (this.hasWarningTarget) {
      this.warningTarget.textContent =
        `Vous allez clôturer définitivement l'exercice ${year}. ` +
        `Aucune écriture manuelle, aucune contre-écriture, aucun nouveau paiement ` +
        `avec date d'encaissement dans cet exercice ne pourra plus être ajouté. ` +
        `Le PDF annuel sera archivé dans votre coffre-fort. ` +
        `Avez-vous bien saisi toutes les recettes de l'exercice ?`;
    }
  }

  _writeSummary(preview) {
    if (this.hasEntriesCountTarget) {
      this.entriesCountTarget.textContent = String(preview.entriesCount ?? 0);
    }
    const totalIncl = preview?.totalAll?.amountInclVat ?? 0;
    if (this.hasTotalInclVatTarget) {
      this.totalInclVatTarget.textContent = this._formatEur(totalIncl);
    }
    if (this.hasFranchiseAmountTarget) {
      const rates = Array.isArray(preview.totalsByVatRate) ? preview.totalsByVatRate : [];
      const franchise = rates.find((r) => this._isFranchise(r.rate));
      this.franchiseAmountTarget.textContent = this._formatEur(
        franchise ? franchise.amountInclVat : 0,
      );
    }
    if (this.hasPaymentsAmountTarget) {
      this.paymentsAmountTarget.textContent = this._formatEur(preview.paymentsAmount ?? 0);
    }
    if (this.hasManualEntriesAmountTarget) {
      this.manualEntriesAmountTarget.textContent = this._formatEur(
        preview.manualEntriesAmount ?? 0,
      );
    }
    if (this.hasCorrectionsAmountTarget) {
      this.correctionsAmountTarget.textContent = this._formatEur(
        preview.correctionsAmount ?? 0,
      );
    }

    this._writeUnconfirmedMonths(preview.unconfirmedMonths ?? []);
  }

  _writeUnconfirmedMonths(months) {
    if (!this.hasUnconfirmedBlockTarget) return;
    if (!Array.isArray(months) || months.length === 0) {
      this.unconfirmedBlockTarget.hidden = true;
      return;
    }
    this.unconfirmedBlockTarget.hidden = false;
    const count = months.length;
    if (this.hasUnconfirmedTitleTarget) {
      this.unconfirmedTitleTarget.textContent = `${count} mois non confirmé${count > 1 ? "s" : ""} sur l'exercice`;
    }
    if (this.hasUnconfirmedIntroTarget) {
      this.unconfirmedIntroTarget.textContent =
        `${count} mois non confirmé${count > 1 ? "s" : ""} sur l'exercice — la clôture sera possible ` +
        `quand même ; auditabilité conservée via le hash de clôture.`;
    }
    if (this.hasUnconfirmedDetailTarget) {
      const labels = months.map((m) => m.label).join(", ");
      this.unconfirmedDetailTarget.textContent = `Mois concernés : ${labels}.`;
    }
  }

  _isFranchise(rate) {
    const n = Number.parseFloat(String(rate));
    return Number.isFinite(n) && n === 0;
  }

  _formatEur(value) {
    const num = typeof value === "number"
      ? value
      : Number.parseFloat(String(value).replace(",", "."));
    if (!Number.isFinite(num)) return "0,00 €";
    return new Intl.NumberFormat("fr-FR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num) + " €";
  }

  _showModal() {
    this.element.hidden = false;
    this.element.scrollIntoView({ behavior: "smooth", block: "start" });
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.focus();
    }
  }

  _hideModal() {
    this.element.hidden = true;
  }

  _setSubmitButtonState(loading) {
    if (!this.hasSubmitButtonTarget) return;
    this.submitButtonTarget.disabled = loading;
    if (loading) {
      this.submitButtonTarget.dataset.originalLabel = this.submitButtonTarget.textContent;
      this.submitButtonTarget.textContent = "Scellement…";
    } else if (this.submitButtonTarget.dataset.originalLabel) {
      this.submitButtonTarget.textContent = this.submitButtonTarget.dataset.originalLabel;
      delete this.submitButtonTarget.dataset.originalLabel;
    }
  }

  _handleApiError(error) {
    if (error instanceof APIError) {
      const data = error.data ?? {};
      const code = data?.error?.code ?? "";
      const serverMessage = data?.error?.message ?? "";
      const mapped = ERROR_MESSAGES[code] ?? serverMessage ?? error.message;
      this._showError(mapped || "Erreur lors de la clôture.");
      return;
    }
    this._showError("Erreur réseau. Veuillez réessayer.");
  }

  _showError(message) {
    if (this.hasErrorBoxTarget && this.hasErrorMessageTarget) {
      this.errorMessageTarget.textContent = message;
      this.errorBoxTarget.hidden = false;
      this.errorBoxTarget.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
    this.showError(message);
  }

  _clearError() {
    if (this.hasErrorBoxTarget) {
      this.errorBoxTarget.hidden = true;
    }
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.textContent = "—";
    }
  }

  _reload() {
    window.location.reload();
  }
}
